import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { failureError, invalidError, notice } from '@/components/pbo/notice'
import { PboConfirm } from '@/components/pbo/pboDialog'
import { getMasterShipperOptions } from '@/services/master/userManagement/apis'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { toSelectOptions, useGetCombos } from '@/services/combo/useComboQueries'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { usePboQueryErrorToast } from '@/hooks/usePboQueryErrorToast'
import { appendedRows, FETCH_ALL_MESSAGES, VIEW_PAGE_SIZE } from '@/hooks/useFetchAllRows'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { shipperScopeOf } from '@/components/system/shipperScope'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { globalMonitoringQueryKeys, updateGlobalMonitorings, useGetGlobalMonitoringList } from './queries/useGetGlobalMonitoringList'
import { downloadMonitoringExcel, downloadMonitoringHistoriesExcel, useGetMonitoringHistories } from './queries/useMonitoringHistories'
import {
  apiErrMessage,
  fromSearchPage,
  fromSearchQuery,
  hasClosingRange,
  isClosingRangeWithinLimit,
  isAxiosTimeout,
  MONITORING_DOWNLOAD_TIMEOUT_MESSAGE,
  toDetailedReason,
  withDefaultClosingRange,
} from './queries/mapping'
import type { GlobalMonitoringParams, GlobalMonitoringRow } from './queries/mapping'
import { BRD_CONTACT_OPTIONS, CENTER_CHK_OPTIONS, GL11_FILTER_LIST_MAX_SIZE, GL11_PAGE_SIZE } from './components/consts'
import GlobalMonitoringPageView, { type ActionCtx, type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'

type EditableRow = GlobalMonitoringRow & { _rowKey: string; key: string }

/** 받은 행 → 편집 행. key 를 안 실으면 편집 왕복 때 그때 보이던(필터된) 인덱스가 박혀 검색을 풀었을 때 겹친다(#777). base = 앞서 받은 행 수. */
const toEditable =
  (base: number) =>
  (row: GlobalMonitoringRow, i: number): EditableRow => ({ ...row, _rowKey: `h-${base + i}`, key: `h-${base + i}` })

/** 상세사유 길이 — 옛 ExcelDownloadReasonModal 계약 그대로(1.0 RemarkPop 파리티 · 감사로그 remark 한도). */
const REASON_DETAIL_MIN = 5
const REASON_DETAIL_MAX = 200

const MESSAGES = {
  noShipper: '화주를 선택해 주세요.',
  noClosingRange: '최초일자를 선택해 주세요.',
  tooLongClosingRange: '최초일자는 최대 31일까지 조회할 수 있습니다. 기간을 줄여 주세요.',
  tooManyFilterValues: `송장번호와 주문번호는 각각 최대 ${GL11_FILTER_LIST_MAX_SIZE}개까지 조회할 수 있습니다. 조건을 줄여 주세요.`,
  noSearchYet: '조회한 뒤 내려받을 수 있습니다.',
  noSelection: '저장할 행을 선택해 주세요.',
  saved: '저장이 완료되었습니다.',
  downloadValidation: `사유코드를 고르고 상세사유를 ${REASON_DETAIL_MIN}글자 이상 입력해 주세요.`,
  downloadTooLong: `상세사유는 ${REASON_DETAIL_MAX}글자까지 입력할 수 있습니다.`,
  noHistory: '다운로드할 이력이 없습니다. 상단 목록에서 행을 선택해 주세요.',
  downloaded: '다운로드 완료',
  uploadUnavailable: '업로드는 아직 지원하지 않습니다.',
  leaveDirty: '저장하지 않은 편집 내용이 있습니다. 저장은 페이지마다 해야 합니다. 이동할까요?',
  conflict: '다른 사용자가 수정했습니다. [조회]를 다시 눌러 최신 상태를 확인해 주세요.',
} as const

/** 오늘(YYYY-MM-DD) — 최초일자 기본값. 데모 픽커 기본(defaultPeriod 'today')과 같은 값이라 표시와 조회가 어긋나지 않는다. */
const todayDash = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** 조회영역 값(키 = filterKey) → BE 조회 조건. 옛 toSearchParams 와 같은 규칙이되 데모 키를 읽는다. */
const toParams = (filters: Record<string, unknown>): GlobalMonitoringParams => {
  const range = filters.CLS_DATE as unknown[] | undefined
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
  const list = (v: unknown) => (Array.isArray(v) && v.length ? v.map(String).filter(Boolean) : undefined)
  const status = text(filters.ORD_REMARK)
  return {
    shipperCode: text(filters.SPR_NM),
    closingDateFrom: Array.isArray(range) ? text(range[0]) : undefined,
    closingDateTo: Array.isArray(range) ? text(range[1]) : undefined,
    invoiceNos: list(filters.INVOICE_NO),
    status: status === '__ALL__' ? undefined : status,
    shipperOrderNos: list(filters.SPR_ORD_NO),
  }
}

/** 조회 조건이 같은가 — 같은 조건이면 캐시라 요청이 안 나가므로 그때만 다시 읽는다(sy10 과 같은 규약). */
const sameParams = (a: GlobalMonitoringParams, b: GlobalMonitoringParams) => JSON.stringify(a) === JSON.stringify(b)

/** 되살린 조회 조건 → 조회영역 값(키 = filterKey). 없는 값은 얹지 않는다(기본값이 살아 있게). */
const toFilterValues = (params: GlobalMonitoringParams | undefined): Record<string, unknown> => {
  if (!params) return {}
  return {
    ...(params.shipperCode ? { SPR_NM: params.shipperCode } : {}),
    ...(params.closingDateFrom && params.closingDateTo ? { CLS_DATE: [params.closingDateFrom, params.closingDateTo] } : {}),
    ...(params.invoiceNos?.length ? { INVOICE_NO: params.invoiceNos } : {}),
    ...(params.status ? { ORD_REMARK: params.status } : {}),
    ...(params.shipperOrderNos?.length ? { SPR_ORD_NO: params.shipperOrderNos } : {}),
  }
}

/**
 * 모니터링/CS(GL11) — 1.0 GL11 을 pbo-core 로 재구현한 화면.
 *
 * 겉(조회영역·위아래 표·확인창·엑셀 사유 모달)은 컨덕터 뷰(`./handoff/page.generated`)가 그리고,
 * 이 파일은 옛 훅·API·세션/URL 유지를 그대로 두고 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드, 컨덕터 문서 51).
 * 옛 화면·표 컴포넌트(`components/Monitoring*.tsx`)가 나눠 갖던 상태를 여기로 모았다 — 확인창·선택·검색어·페이저는 뷰가 가진다.
 * 옛 플래그 분기(useFeatureFlag + v2/page)는 없앴다 — 화면은 하나다(2026-09-11 규칙).
 */
function GlobalMonitoring() {
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()
  // 마운트 스냅샷은 useRef 로 — useMemo 는 캐시라 React 가 언제든 버릴 수 있다.
  const restoredRef = useRef<GlobalMonitoringParams | undefined>()
  const restoredReadRef = useRef(false)
  if (!restoredReadRef.current) {
    restoredReadRef.current = true
    restoredRef.current = fromSearchQuery(parsedQs)
  }
  const restoredPageRef = useRef(fromSearchPage(parsedQs))

  /** 화주 옵션 — 유저관리와 같은 API 재사용({value: shipperCode, label: shipperName}). */
  const { data: shipperOptions = [] } = useQuery({ queryKey: ['GL11_SHIPPER_OPTIONS'], queryFn: getMasterShipperOptions })
  // 기본 화주 = 현재 세션 화주(shipper_code — 화주 전환을 반영한다, LOGISPRD-14651). 옵션에 없으면 첫 화주로 폴백한다.
  const sessionShipperRef = useRef<string>()
  sessionShipperRef.current ??= shipperScopeOf(AuthStorage.user.get()).shipperCode
  const defaultShipper = useMemo(
    () => String((shipperOptions.find((o) => String(o.value) === sessionShipperRef.current) ?? shipperOptions[0])?.value ?? ''),
    [shipperOptions],
  )

  /**
   * 복원 조건으로 같은 캐시 key 를 만들되, 마운트만으로 서버 조회는 하지 않는다 — [조회]를 눌러야 켜진다(armed).
   * 꺼진 동안에도 캐시는 보인다(탭 복귀). 전량을 구간으로 받는다(LOGISPRD-15271) — 페이지 이동은 받은 행 안에서라 다시 조회하지 않는다.
   * 실패 안내는 훅이 전역 알럿으로 한 번 낸다.
   */
  const [params, setParams] = useState<GlobalMonitoringParams>(restoredRef.current ?? {})
  // 캐시가 있으면(탭 복귀) 켜 둔다 — 받다 만 캐시면 남은 구간을 이어 받아야 받는 중 차단이 풀린다. 캐시가 없으면 [조회] 전까지 끈다.
  const queryClient = useQueryClient()
  const [armed, setArmed] = useState(() => !!restoredRef.current && queryClient.getQueryData(globalMonitoringQueryKeys.list(restoredRef.current)) !== undefined)
  const headPageRef = useRef(restoredPageRef.current)
  /** 받는 중에 온 상단 페이지 통지 — 받기가 끝나면 저장한다(아래 flush effect, LOGISPRD-15349) */
  const pendingHeadPageRef = useRef<number | null>(null)
  const { rows: fetchedRows, isFirstLoading, isFetchingRest, isPartial, guardAction, refetch } = useGetGlobalMonitoringList(params, armed && !!params.shipperCode)

  /** 편집 상태 — 받은 행 전체에 편집 셀 변경을 얹는다. 저장은 현재 페이지 선택 행만 나간다. */
  const [headRows, setHeadRows] = useState<EditableRow[]>([])
  const dirtyRef = useRef(false)
  const prevFetchedRef = useRef<GlobalMonitoringRow[]>([])
  /** [초기화] 뒤 다음 [조회] 전까지 — 이미 조회된 표를 그대로 둔다(1.0 공통·옛 화면과 같다). 조건이 비어 새 키의 받은 행이 비어도 덮지 않는다. */
  const resetRef = useRef(false)
  useEffect(() => {
    if (resetRef.current) return
    // 남은 구간이 붙은 것이면 붙은 행만 덧붙인다 — 받는 동안 한 편집·dirty 를 지우지 않는다.
    const tail = appendedRows(prevFetchedRef.current, fetchedRows)
    if (tail) {
      const base = prevFetchedRef.current.length
      setHeadRows((prev) => [...prev, ...tail.map(toEditable(base))])
    } else {
      setHeadRows(fetchedRows.map(toEditable(0)))
      dirtyRef.current = false
    }
    prevFetchedRef.current = fetchedRows
  }, [fetchedRows])

  /** 마스터-디테일 — 상단에서 고른 행의 송장번호로 하단 이력을 조회한다(뷰의 onSelect 슬롯). */
  const [masterInvoice, setMasterInvoice] = useState('')
  const [masterClosingDate, setMasterClosingDate] = useState('')
  const historyShipper = String(params.shipperCode ?? '')
  const historyQuery = useGetMonitoringHistories(historyShipper, masterInvoice, masterClosingDate || undefined)
  const { data: histories = [] } = historyQuery
  usePboQueryErrorToast(historyQuery)

  /** 엑셀 다운로드 사유코드 — 서버 콤보. 명(COMBO_OPT_NM)이 비면 코드로 대체한다(ST02 와 같은 규약). */
  const { data: exportCombo } = useGetExportCombo({ errorToast: true })
  const reasonOptions = useMemo<Option[]>(
    () =>
      (exportCombo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code, i) => ({
        value: code,
        label: exportCombo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[i] || code,
      })),
    [exportCombo],
  )
  /**
   * 분류·이슈사유·내부 확인상태 선택지 — 서버 콤보(1.0 메타 MOMS_COMBO_INFO, 1.0 분류 필터도 이 콤보). BE 필터는 분류를 정확일치로 건다
   * (GlobalInvoiceMonitoringRepository `status`). 1.0 콤보에도 스캔 결과 `재출력` 은 없다 — 1.0 과 같게 둔다.
   */
  const { data: combos } = useGetCombos(['ORD_REMARK', 'ISSUE_REASON', 'INTL_CHK_STAT'], { errorToast: true })
  const comboOptions = useMemo(
    () => ({
      ordRemark: toSelectOptions(combos?.ORD_REMARK),
      issueReason: toSelectOptions(combos?.ISSUE_REASON),
      intlChkStat: toSelectOptions(combos?.INTL_CHK_STAT),
    }),
    [combos],
  )

  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const [downloading, setDownloading] = useState(false)
  const isBusy = saving || downloading

  /** 마지막 조회 조건을 메뉴 세션·URL 에 남긴다. reload() 를 빼면 탭 스트립이 옛 조건을 되살린다. */
  const persist = useCallback(
    (next: GlobalMonitoringParams, page: number) => {
      const stored = { ...next, page }
      MenuStorage.menuOnSession.setSearch(stored)
      menuSession.reload()
      replaceQuery(stored)
    },
    [menuSession, replaceQuery],
  )

  /** 저장 대상 — 선택 행을 편집 반영본에서 되찾는다(뷰가 넘긴 행은 표시용 사본이다). */
  const selectedRows = useCallback(
    (ctx: ActionCtx): EditableRow[] => {
      const picked = new Set(ctx.selected.map((r) => (typeof r._rowKey === 'string' ? r._rowKey : '')))
      return headRows.filter((r) => picked.has(r._rowKey))
    },
    [headRows],
  )

  // ── 뷰 배선(Wiring) — 배선 객체는 매 렌더 새로 만든다(문서 51). ──
  /** 페이지 이동 확인창이 떠 있는가 — 대기 중 재클릭을 막는다. */
  const leaveConfirmOpenRef = useRef(false)
  /** 상단 페이지를 URL·탭 세션에 남긴다 — 받은 행 안에서라 다시 조회하지 않는다. */
  const saveHeadPage = (next: number) => {
    if (next === headPageRef.current) return
    headPageRef.current = next
    if (params.shipperCode) persist(params, next)
  }
  // 받기가 끝나면 받는 중 미뤄 둔 페이지를 저장한다 — 끝난 렌더에서 뷰가 다시 알렸으면 onState 가 먼저 비워 두어 여기선 할 일이 없다(자식 effect 가 먼저 돈다).
  useEffect(() => {
    if (isFirstLoading || isFetchingRest || pendingHeadPageRef.current == null) return
    const next = pendingHeadPageRef.current
    pendingHeadPageRef.current = null
    saveHeadPage(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 받기가 끝나는 순간에만 돈다(saveHeadPage 는 매 렌더 새로 만든다)
  }, [isFirstLoading, isFetchingRest])

  const wiring: Wiring = {
    permission: usePermissionSlot('gl13'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { gl11HeadTable: coreTableLayoutSlot('GL13_HEAD'), gl11ItemTable: coreTableLayoutSlot('GL13_ITEM') },
    rows: {
      // total 없이 주면 뷰가 1만 건씩 페이지를 자르고 목록 검색을 전량에 건다(total 을 주면 서버 페이징 — 생성본 `ROWS_SERVERPAGED`)
      //  — 첫 구간·나머지 구간·재조회 중에는 받은 행 위에 로더를 띄워 표를 막는다(LOGISPRD-15390). 받는 만큼 '전체 N'·페이지 수가 늘어나는 게 진행 표시다(LOGISPRD-15271).
      ROWS: () => ({ rows: headRows as unknown as Row[], loading: isFirstLoading || isFetchingRest }),
      // 이력은 서버가 전량으로 준다 — 페이지 나누기는 뷰가 한다(배열을 돌려주면 클라이언트 페이징).
      ROWS2: () => histories as unknown as Row[],
    },
    actions: {
      /** [조회] — 조회영역 값을 BE 조건으로. 최초일자는 필수다(옛 MonitoringFilter 검증). */
      search: async (filters) => {
        const next = toParams(filters)
        if (!next.shipperCode) {
          notice.invalid(MESSAGES.noShipper)
          return
        }
        if (!hasClosingRange(next)) {
          notice.invalid(MESSAGES.noClosingRange)
          return
        }
        if (!isClosingRangeWithinLimit(next)) {
          notice.invalid(MESSAGES.tooLongClosingRange)
          return
        }
        if ((next.invoiceNos?.length ?? 0) > GL11_FILTER_LIST_MAX_SIZE || (next.shipperOrderNos?.length ?? 0) > GL11_FILTER_LIST_MAX_SIZE) {
          notice.invalid(MESSAGES.tooManyFilterValues)
          return
        }
        persist(next, 1)
        headPageRef.current = 1
        pendingHeadPageRef.current = null
        // [초기화]로 남겨 둔 표는 새 조회 결과로 바뀌어야 한다 — 새 조건 첫 구간이 실패해도(받은 행 [] 그대로라 effect 가 안 돈다) 옛 표가 남지 않게 먼저 비운다.
        if (resetRef.current) setHeadRows([])
        resetRef.current = false
        const same = sameParams(next, params)
        if (same) {
          // 같은 조건 [조회]는 편집을 버린다(1.0) — 응답이 같으면 받은 행 참조가 그대로라 effect 가 안 돌아, 누르는 자리에서 받은 행으로 되돌린다(#1017 리뷰).
          setHeadRows(fetchedRows.map(toEditable(0)))
          dirtyRef.current = false
          prevFetchedRef.current = fetchedRows
        } else {
          prevFetchedRef.current = [] // 새 조건은 통째로 바꾼다
        }
        // 조건이 바뀌면 그 키의 지난 캐시(1분)를 지우고 옮긴다 — 안 지우면 이전 조건·[초기화] 뒤 같은 조건으로 돌아왔을 때 다시 읽지 않는다(ST09 와 같다).
        if (!same) queryClient.removeQueries({ queryKey: globalMonitoringQueryKeys.list(next), exact: true })
        // 클릭 문맥이라 flushSync — 조건이 커밋되기 전에 다시 읽으면 이전 조건으로 읽는다.
        flushSync(() => {
          setMasterInvoice('')
          setMasterClosingDate('')
          setParams(next)
          setArmed(true)
        })
        // 같은 조건이어도 다시 읽는다(1.0 동작) — 같은 키는 캐시라 켜기만으로는 안 나간다. 조건이 바뀌면 새 키가 스스로 받는다.
        if (same) await refetch()
      },
      /** 편집 셀 — 표는 보이는 행만 돌려준다. 행 키로 찾아 바뀐 것만 갈아 끼운다. */
      ROWS_rowsChange: (changed) => {
        dirtyRef.current = true
        const byKey = new Map(changed.map((r) => [typeof r._rowKey === 'string' ? r._rowKey : '', r]))
        setHeadRows((prev) => prev.map((r) => ({ ...r, ...(byKey.get(r._rowKey) ?? {}) }) as EditableRow))
      },
      /** [저장] 확인 — 선택 행의 CS 편집 필드를 전체 치환 저장한다. 분류는 '기타'일 때만 서버가 반영한다(1.0 규칙, 서버 강제). */
      gl11SaveConfirm_ok: async (ctx) => {
        // 받는 중·일부 실패면 안내하고 확인창을 유지한다(throw — false 는 생성본이 성공으로 읽는다)
        if (!guardAction()) throw new Error(FETCH_ALL_MESSAGES.fetching)
        // state 반영 전 같은 이벤트 틱에서 확인을 연속 클릭해도 저장 요청은 한 번만 보낸다.
        if (savingRef.current) return false
        const targets = selectedRows(ctx)
        if (targets.length === 0) {
          throw invalidError(MESSAGES.noSelection)
        }
        savingRef.current = true
        setSaving(true)
        let listRefreshFailed = false
        try {
          await updateGlobalMonitorings(targets)
          // 저장했으니 편집 표시를 먼저 내린다 — 재조회가 안 나가는 경우(초기화 뒤처럼 조회가 비활성)에도
          //  dirty 가 남아 페이지 이동 때 "저장하지 않은 편집" 확인창이 잘못 떴다(#785 봇 리뷰).
          dirtyRef.current = false
          // 1.0 저장 후 재조회 — 서버 반영값(분류 규칙·수정자·수정일)으로 갱신한다.
          // 초기화 뒤에는 조건이 없어 새 rowVersion을 받을 수 없다. 성공한 옛 행을 남겨
          // 다시 저장하면 409가 되므로, 다시 조회할 조건이 없을 때만 표를 비운다.
          if (params.shipperCode) {
            setArmed(true) // 캐시로 복원된 목록이면 꺼져 있다 — 켜야 첫 구간 뒤 나머지 구간도 이어 받는다
            prevFetchedRef.current = [] // 저장 뒤 재조회는 통째로 바꾼다(구조 공유로 '뒤에 붙음'으로 읽지 않게)
            const refreshed = await refetch()
            if (refreshed.isError && refreshed.error) {
              // 목록 실패는 훅이 전역 알럿으로 한 번 알린다. 아래 저장 실패 알럿을 겹쳐 띄우지 않는다.
              listRefreshFailed = true
              throw refreshed.error
            }
          } else {
            setHeadRows([])
            setMasterInvoice('')
            setMasterClosingDate('')
          }
          return MESSAGES.saved
        } catch (e) {
          if (listRefreshFailed) throw e
          const status = (e as { response?: { status?: number } })?.response?.status
          throw failureError(status === 409 ? MESSAGES.conflict : apiErrMessage(e))
        } finally {
          savingRef.current = false
          setSaving(false)
        }
      },
      /** 상단 엑셀 — 조회 조건 기준(/excel). */
      gl11HeadDownloadModal_ok: (ctx) => runDownload(ctx, 'head'),
      /** 하단 엑셀 — 선택한 송장의 이력 기준(/histories/excel). 1.0 GL11_ITEM TOOL_EXP=1 파리티. */
      gl11ItemDownloadModal_ok: (ctx) => runDownload(ctx, 'item'),
      /**
       * 엑셀 업로드 [확인] — 데모에만 있는 모달이다(1.0 GL11 에도, 2.0 API 에도 업로드가 없다).
       * 여는 버튼이 데모에 없어 지금은 화면에서 닿지 않는다 — 계약상 슬롯만 채운다.
       * TODO(api): 업로드가 실제 기능이면 moms-be 엔드포인트가 먼저다. 그전까지 여기서 막는다.
       */
      gl11UploadModal_ok: () => {
        throw invalidError(MESSAGES.uploadUnavailable)
      },
    },
    /**
     * 상단에서 **행을 클릭해** 고른 송장으로 하단 이력을 읽는다(onPick).
     * 체크 선택(onSelect)이 아니다 — 뷰의 하단 표도 같은 pick 으로 걸린다(page.generated ROWS2Visible).
     * 두 신호를 갈라 쓰면 행을 클릭했을 때 표는 걸리는데 조회가 안 나가 하단이 빈다(2026-09-19 사전 리뷰).
     */
    onPick: (rows) => {
      const invoice = rows[0]?.INVOICE_NO
      const closingDate = rows[0]?.CLS_DATE
      setMasterInvoice(typeof invoice === 'string' ? invoice : '')
      setMasterClosingDate(typeof closingDate === 'string' ? closingDate : '')
    },
    options: {
      GL11SHIPPER_OPTIONS: () => shipperOptions as Option[],
      // 분류·편집 셀 선택지 — 콤보가 있는 칸은 서버 콤보, 없는 칸은 화면 상수(components/consts.ts). 생성본 데모는 쓰지 않는다.
      GL11ORDREMARK_OPTIONS: () => comboOptions.ordRemark,
      ROWS_ORD_REMARK: () => comboOptions.ordRemark,
      ROWS_ISSUE_REASON: () => comboOptions.issueReason,
      ROWS_BRD_CONTACT: () => BRD_CONTACT_OPTIONS,
      ROWS_INTL_CHK_STAT: () => comboOptions.intlChkStat,
      ROWS_CENTER_CHK: () => CENTER_CHK_OPTIONS,
      // 엑셀 사유코드는 서버 콤보(DOWNLOAD_CD)다 — 데모 옵션을 그대로 쓰면 감사로그에 없는 코드가 실린다
      //  (옛 ExcelDownloadReasonModal·ST02 와 같은 규약). 위/아래 표 모달이 같은 목록을 쓴다.
      REASONRADIO_OPTIONS: () => reasonOptions,
      REASONRADIO_OPTIONS2: () => reasonOptions,
    },
    filterDefaults: { gl11FilterCard: (demo) => ({ ...demo, SPR_NM: defaultShipper || undefined }) },
    filterInitial: {
      gl11FilterCard: (defaults) => ({
        ...defaults,
        ...toFilterValues(restoredRef.current ? withDefaultClosingRange(restoredRef.current, todayDash()) : undefined),
      }),
    },
    useBusy: () => isBusy,
    opens: {
      hdExcelButton: () => {
        if (!params.shipperCode) {
          notice.invalid(MESSAGES.noSearchYet)
          return false
        }
        return true
      },
      // 하단 이력은 상단에서 행을 골라야 생긴다 — 컨텍스트(화주+송장)가 없으면 열지 않는다.
      itExcelButton: () => {
        if (!historyShipper || !masterInvoice) {
          notice.invalid(MESSAGES.noHistory)
          return false
        }
        return true
      },
    },
    stateInitial: { pager: { 'hd-pager': { page: restoredPageRef.current, size: VIEW_PAGE_SIZE }, 'it-pager': { page: 1, size: GL11_PAGE_SIZE } } },
    /** [초기화] — 조회 조건(세션·URL)만 비운다. 이미 조회된 표는 그대로 둔다(1.0 공통·옛 화면과 같다). */
    onReset: () => {
      MenuStorage.menuOnSession.setSearch({})
      menuSession.reload()
      replaceQuery({})
      headPageRef.current = 1
      pendingHeadPageRef.current = null
      resetRef.current = true
      // 받다 만 표를 남기면 빈 조건 기준이라 받는 중 차단(guardAction)이 풀린 채 덜 받은 목록 위에서 저장이 열린다 — 그때는 비운다.
      if (isFetchingRest || isPartial) {
        setHeadRows([])
        dirtyRef.current = false
      }
      setMasterInvoice('')
      setMasterClosingDate('')
      setParams({})
    },
    /**
     * 페이지를 옮기기 **전** — 미저장 편집이 있으면 물어보고, 취소하면 옮기지 않는다.
     * 옛 구현(지운 MonitoringTables `onHeadPage`)이 window.confirm 으로 지키던 선이다.
     * 저장은 현재 페이지 선택 행만 나가므로 옮기기 전에 알린다 — onState 는 이미 바뀐 뒤 통지라 못 막는다.
     * 신규 디자인 확인창(PboConfirm)으로 묻는다 — 생성본 페이저가 Promise 답을 기다렸다 옮긴다(컨덕터 codegen #702, 기타출고와 같은 모양).
     */
    canChangePage: ({ pager }) => {
      if (pager !== 'hd-pager' || !dirtyRef.current) return true
      // 확인창이 떠 있는 동안 페이저를 또 누르면 무시한다 — 확인창이 겹치고 두 답이 모두 이동(go)을 돌리지 않게(#899 리뷰 sookeunwoo).
      if (leaveConfirmOpenRef.current) return false
      leaveConfirmOpenRef.current = true
      return new Promise<boolean>((resolve) => {
        const answer = (ok: boolean) => {
          leaveConfirmOpenRef.current = false
          resolve(ok)
        }
        PboConfirm.show(MESSAGES.leaveDirty, { onConfirm: () => answer(true), onClose: () => answer(false), okText: '이동' })
      })
    },
    /** 상단 페이지 이동 — 받은 행 안에서라 다시 조회하지 않고 URL 에만 남긴다(위 canChangePage 를 지난 뒤에만 온다). */
    onState: (s) => {
      const next = s.pager['hd-pager'].page
      // 받는 중에는 저장을 미룬다 — 덜 온 행으로 뷰가 마지막 페이지로 줄여 알린 값이 복원 페이지를 덮는다(LOGISPRD-15349).
      //  받는 중 사용자가 옮긴 페이지도 여기로 오므로 버리지 않고 마지막 값을 두었다가 받기가 끝나면 저장한다.
      if (isFirstLoading || isFetchingRest) {
        pendingHeadPageRef.current = next
        return
      }
      pendingHeadPageRef.current = null
      saveHeadPage(next)
    },
  }

  /** 엑셀 — 사유코드·상세사유를 검증하고 서버 xlsx 를 내려받는다(EXPORT 감사 기록). */
  async function runDownload(ctx: ActionCtx, kind: 'head' | 'item'): Promise<string> {
    const reason = typeof ctx.form?.reason === 'string' ? ctx.form.reason : ''
    const detail = typeof ctx.form?.detail === 'string' ? ctx.form.detail.trim() : ''
    // 옛 모달은 [확인] 을 잠가 막았다 — 생성본 모달에는 그 잠금이 없어(컨덕터 후속) 여기서 같은 선을 지킨다.
    if (!reason || detail.length < REASON_DETAIL_MIN) {
      throw invalidError(MESSAGES.downloadValidation)
    }
    if (detail.length > REASON_DETAIL_MAX) {
      throw invalidError(MESSAGES.downloadTooLong)
    }
    setDownloading(true)
    try {
      if (kind === 'item') {
        await downloadMonitoringHistoriesExcel(
          historyShipper,
          masterInvoice,
          masterClosingDate || undefined,
          toDetailedReason({ reasonCode: reason, reasonDetail: detail }),
        )
      } else await downloadMonitoringExcel(params, toDetailedReason({ reasonCode: reason, reasonDetail: detail }))
      return MESSAGES.downloaded
    } catch (e) {
      throw failureError(isAxiosTimeout(e) ? MONITORING_DOWNLOAD_TIMEOUT_MESSAGE : apiErrMessage(e))
    } finally {
      setDownloading(false)
    }
  }

  // 타이틀 행은 뷰가 그린다(그룹 cmp.TitleRow) — 여기서 또 그리면 제목이 두 번 선다.
  //  key: 화주 옵션이 비동기라 첫 렌더의 조회영역 기본값에는 화주가 없다(뷰는 filterDefaults 를 마운트 때 한 번만 읽는다).
  //   옵션이 도착하면 조회영역을 다시 세워 기본 화주가 칸에 보이게 한다 — 옛 화면도 같은 방식이었다
  //   (components/MonitoringFilter.tsx 의 `key={defaultShipper ?? 'loading'}`). 안 그러면 조회는 세션 화주로 나가는데
  //   칸에는 데모 기본값이 남아 표시와 조회가 어긋난다(2026-09-19 사전 리뷰 Warning).
  return <GlobalMonitoringPageView key={defaultShipper || 'loading'} wiring={wiring} />
}

export default GlobalMonitoring
